import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PrimaryButton } from './Button'
import Radom, { TransactionKind, WithdrawalStatus } from '../state/Radom'
import { InputLabel } from './Input'
import { EXPORT_COLUMN_TYPES, EXPORT_DEPOSITS_COLUMNS, EXPORT_PAYEMNTS_TYPE, EXPORT_PAYMENTS_COLUMNS, EXPORT_PAYOUTS_COLUMNS, EXPORT_TIME_RANGES, EXPORT_WITHDRAWALS_COLUMNS, RADOM_COLORS } from '../util/Constants'
import Checkbox from './Checkbox'
import Dropdown, { DropdownItem } from './Dropdown'
import { endOfDay, intlFormat, isBefore, startOfDay } from 'date-fns'
import { downloadBlob, errorToast, getExportFileName, useTestMode } from '../util/Util'
import DateSelector from './DateSelector'
import { camelCase, cloneDeep } from 'lodash'
import { DateRange } from './Shared'

const ExportTransactionsForm = (): ReactElement => {
  const EXPORT_COLUMS = {
    [TransactionKind.Deposit]: EXPORT_DEPOSITS_COLUMNS,
    [TransactionKind.Withdrawal]: EXPORT_WITHDRAWALS_COLUMNS,
    [TransactionKind.Payout]: EXPORT_PAYOUTS_COLUMNS,
    [TransactionKind.Payment]: EXPORT_PAYMENTS_COLUMNS
  }

  const [closeColumnsFn, setCloseColumnsFn] = useState(() => () => {})
  const [paymentTypeCloseFn, setPaymentTypeCloseFn] = useState(() => () => {})
  const [transactionKindCloseFn, setTransactionKindCloseFn] = useState(() => () => {})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState(EXPORT_TIME_RANGES[0])
  const [transactionKind, setTransactionKind] = useState<TransactionKind | undefined>()
  const [customFromDate, setCustomFromDate] = useState<Date>()
  const [customToDate, setCustomToDate] = useState<Date>()
  const [paymentType, setPaymentType] = useState<EXPORT_PAYEMNTS_TYPE>(EXPORT_PAYEMNTS_TYPE.ALL_PAYMENTS)
  const [columnTypes, setColumnTypes] = useState<EXPORT_COLUMN_TYPES>(EXPORT_COLUMN_TYPES.ALL_COLUMNS)
  const [columns, setColumns] = useState<Record<string, { label: string, unchecked?: boolean }> | undefined>()
  const [isTestMode] = useTestMode()
  const [withdrawalStatuses, setWithdrawalStatuses] = useState<WithdrawalStatus[]>([
    WithdrawalStatus.Complete,
    WithdrawalStatus.Failed,
    WithdrawalStatus.Pending,
    WithdrawalStatus.Processing
  ])

  const columnsAsStrings = useMemo(() => columns
    ? Object.entries(columns).filter(([_, v]) => !v.unchecked).map(([k]) => k)
    : [], [columns])

  const exportTransactions = async (): Promise<void> => {
    if (!transactionKind || !columns) return
    let fromDate: string | undefined, toDate: string | undefined
    if (selectedTimeRange.label === 'Custom') {
      if (customFromDate && customToDate) {
        fromDate = startOfDay(customFromDate).toISOString()
        toDate = endOfDay(customToDate).toISOString()
      } else {
        errorToast('Must select from and to custom date ranges')
        return
      }
    } else if (selectedTimeRange.dateFn) {
      fromDate = selectedTimeRange.dateFn().from.toISOString()
      toDate = selectedTimeRange.dateFn().to.toISOString()
    } else {
      fromDate = new Date(1970, 0, 1).toISOString()
      toDate = new Date().toISOString()
    }
    setIsLoading(true)

    if (transactionKind === TransactionKind.Deposit) {
      Radom.exportDeposits(
        isTestMode,
        fromDate,
        toDate,
        columnsAsStrings
      )
        .then(data => {
          const blob = new Blob(
            [data],
            { type: 'text/csv' }
          )
          const fileName = getExportFileName('deposits')

          downloadBlob(blob, fileName)
        })
        .catch(err => {
          errorToast(err.response?.data?.error || err.message)
        }).finally(() => {
          setIsLoading(false)
        })
    } else if (transactionKind === TransactionKind.Payment) {
      Radom.exportPayments(
        fromDate,
        toDate,
        columnsAsStrings,
        camelCase(paymentType)
      )
        .then(data => {
          const blob = new Blob(
            [data],
            { type: 'text/csv' }
          )

          const fileName = getExportFileName('payments')

          downloadBlob(blob, fileName)
        })
        .catch(err => {
          errorToast(err.response?.data?.error || err.message)
        }).finally(() => {
          setIsLoading(false)
        })
    } else if (transactionKind === TransactionKind.Payout) {
      Radom.exportPayouts(
        isTestMode,
        fromDate,
        toDate,
        columnsAsStrings
      )
        .then(data => {
          const blob = new Blob(
            [data],
            { type: 'text/csv' }
          )

          const fileName = getExportFileName('payouts')

          downloadBlob(blob, fileName)
        })
        .catch(err => {
          errorToast(err.response?.data?.error || err.message)
        }).finally(() => {
          setIsLoading(false)
        })
    } else {
      Radom.exportWithdrawals(
        fromDate,
        toDate,
        columnsAsStrings,
        withdrawalStatuses
      )
        .then(data => {
          const blob = new Blob(
            [data],
            { type: 'text/csv' }
          )

          const fileName = getExportFileName('withdrawals')

          downloadBlob(blob, fileName)
        })
        .catch(err => {
          errorToast(err.response?.data?.error || err.message)
        }).finally(() => {
          setIsLoading(false)
        })
    }
  }

  useEffect(() => {
    if (!customFromDate || !customToDate) return
    const from = startOfDay(customFromDate)
    const to = startOfDay(customToDate)

    if (isBefore(to, from)) {
      setCustomToDate(undefined)
    }
  }, [customFromDate])

  useEffect(() => {
    setColumnTypes(EXPORT_COLUMN_TYPES.ALL_COLUMNS)
    if (!transactionKind) {
      setColumns(undefined)
      return
    }
    const currentColumns = (transactionKind !== TransactionKind.Payment
      ? EXPORT_COLUMS[transactionKind]
      : EXPORT_COLUMS[transactionKind][paymentType]) as
                            Record<string, { label: string, unchecked?: boolean }>
    setColumns(cloneDeep(currentColumns))
  }, [transactionKind, paymentType])

  return <>
    <div style={{
      padding: '0 30px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      fontSize: 14,
      width: 800
    }}>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <InputLabel>Date range</InputLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {EXPORT_TIME_RANGES.map(t =>
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

                <DateRange onClick={() => setSelectedTimeRange(t)} disabled={isLoading}>
                  <Checkbox isRadio checked={selectedTimeRange === t} size={18} disabled={isLoading} />
                  <span>{t.label}</span>

                </DateRange>
                {
                  selectedTimeRange.label === 'Custom' && t.label === 'Custom' &&
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <DateSelector onChange={d => setCustomFromDate(d)} value={customFromDate} max={new Date()}/>
                  <span>-</span>
                  <DateSelector
                    onChange={d => setCustomToDate(d)}
                    value={customToDate}
                    min={customFromDate}
                    max={new Date()}/>
                </div>
                }
              </div>
              {
                t.dateFn && t.label !== 'All' && t.label !== 'Today' &&
                 <div style={{ display: 'flex', opacity: 0.5 }}>
                   <span>{intlFormat(t.dateFn?.().from)}</span>
                   <span style={{ margin: '0 5px' }}>-</span>
                   <span>{intlFormat(t.dateFn?.().to)}</span>
                 </div>
              }
              {
                t.label === 'Today' && <div style={{ opacity: 0.5 }}>Today</div>
              }
              {
                t.label === 'All' && <div />
              }
            </>
          )}
        </div>
      </div>

      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_MED, margin: '10px 0' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>

          <InputLabel>Transaction Type</InputLabel>
          <Dropdown
            selectedContent={<div style={{ fontSize: '14px' }}>{transactionKind || 'Select transaction type'}</div>}
            onCloseFn={_closeFn => setTransactionKindCloseFn(() => _closeFn)}
            disabled={isLoading}
            dropdownContent={
              <div>
                {Object.values(TransactionKind).map((k) => (
                  <DropdownItem
                    key={k}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                    onClick={() => {
                      if (transactionKind === k) {
                        setTransactionKind(undefined)
                      } else {
                        setTransactionKind(k)
                      }
                      transactionKindCloseFn()
                    }}
                  >
                    <Checkbox size={16} checked={transactionKind === k} />
                    <span>{k}</span>
                  </DropdownItem>
                ))}
              </div>
            }
          />
        </div>
        { transactionKind === TransactionKind.Payment &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>
            <InputLabel>Export Payment Type</InputLabel>
            <Dropdown
              disabled={isLoading}
              selectedContent={<span>{paymentType}</span>}
              onCloseFn={_closeFn => setPaymentTypeCloseFn(() => _closeFn)}
              dropdownContent={
                <div style={{ fontSize: 14 }}>
                  {
                    Object.values(EXPORT_PAYEMNTS_TYPE).map((type, i) =>
                      <DropdownItem
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation()
                          setPaymentType(type)
                          paymentTypeCloseFn()
                        }}
                      >
                        {type}
                      </DropdownItem>
                    )
                  }
                </div>
              } />
          </div>
        }
        { transactionKind === TransactionKind.Withdrawal &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>
            <InputLabel>Withdrawal status</InputLabel>
            <Dropdown
              selectedContent={
                <div style={{ fontSize: '14px' }}>
                  {
                    withdrawalStatuses.length === Object.values(WithdrawalStatus).length
                      ? 'All withdrawals'
                      : withdrawalStatuses.length === 0
                        ? 'Choose withdrawal status'
                        : withdrawalStatuses.map(k => k.charAt(0).toUpperCase() + k.slice(1).toLowerCase()).join(', ')
                  }
                </div>}
              disabled={isLoading}
              dropdownContent={
                <div>
                  {Object.values(WithdrawalStatus).map((k) => (
                    <DropdownItem
                      key={k}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                      onClick={() => {
                        if (withdrawalStatuses.some(item => item === k)) {
                          setWithdrawalStatuses(prev => prev.filter(item => item !== k))
                        } else {
                          setWithdrawalStatuses(prev => [...prev, k])
                        }
                      }}
                    >
                      <Checkbox size={16} checked={withdrawalStatuses.some(item => item === k)} />
                      <span>{k.charAt(0).toUpperCase() + k.slice(1).toLowerCase()}</span>
                    </DropdownItem>
                  ))}
                </div>
              }
            />
          </div>
        }
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 'fit-content' }}>
            <InputLabel>Columns</InputLabel>
            <Dropdown
              disabled={isLoading || !transactionKind}
              selectedContent={<span>{columnTypes}</span>}
              onCloseFn={_closeFn => setCloseColumnsFn(() => _closeFn)}
              dropdownContent={
                <div style={{ fontSize: 14 }}>
                  {
                    Object.values(EXPORT_COLUMN_TYPES).filter(c => c !== columnTypes).map((c, i) =>
                      <DropdownItem key={i} onClick={() => {
                        if (!transactionKind) return
                        if (c === EXPORT_COLUMN_TYPES.ALL_COLUMNS) {
                          const currentColumns = (transactionKind !== TransactionKind.Payment
                            ? EXPORT_COLUMS[transactionKind]
                            : EXPORT_COLUMS[transactionKind][paymentType]) as
                            Record<string, { label: string, unchecked?: boolean }>
                          setColumns(cloneDeep(currentColumns))
                        }
                        closeColumnsFn()
                        setColumnTypes(c)
                      }}>{c}</DropdownItem>
                    )
                  }
                </div>
              } />
          </div>
          {
            columnTypes === 'Custom' && columns &&
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 10, columnGap: 10, padding: 5 }}>
                  {
                    Object.entries(columns).map(([columnKey, columnValue]) =>
                      <div key={columnKey} style={{ fontSize: 14, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                        <Checkbox size={18} checked={!columnValue.unchecked} disabled={isLoading}
                          onClick={() => {
                            columnValue.unchecked = !columnValue.unchecked
                            setColumns(cloneDeep(columns))
                          }} />
                        <span>{columnValue.label}</span>
                      </div>
                    )
                  }
                </div>
          }
          {
            columnTypes === 'All columns' && columns &&
                <span>
                  {Object.entries(columns).map(([_, l]) => l.label).join(', ')}
                </span>
          }
        </div>

      </div>

    </div>
    <div style={{ padding: 30 }}>
      <PrimaryButton
        style={{ width: '100%' }}
        onClick={exportTransactions}
        isLoading={isLoading}
        disabled={
          !transactionKind ||
          isLoading ||
          (transactionKind === TransactionKind.Withdrawal && withdrawalStatuses.length < 1) ||
          columnsAsStrings.length < 1
        }>
      Export transactions
      </PrimaryButton>
    </div>
  </>
}

export default ExportTransactionsForm
